import React from 'react';
import { TypeSectionBanner } from "../../@types/sectionTypes";
import { BANNER_TYPE } from "../../../globals/constants";
import SectionInnerPageBanner from "../../sections/common/banner";
import PolicyLayout from "../../components/PolicyLayout";

const EmployeeCodeOfConductPage = () => {
    const bannerData: TypeSectionBanner = {
        title: "Employee Code of Conduct",
        crumb: "Code of Conduct",
        image: "/assets/images/banner/1.jpg"
    };

    const sections = [
        { id: "introduction", title: "Introduction" },
        { id: "core-values", title: "Core Values" },
        { id: "professional-standards", title: "Professional Standards" },
        { id: "client-relations", title: "Client Relations" },
        { id: "confidentiality", title: "Confidentiality and Data Protection" },
        { id: "conflict-of-interest", title: "Conflict of Interest" },
        { id: "workplace-behavior", title: "Workplace Behavior" },
        { id: "technology-use", title: "Technology and Resource Use" },
        { id: "financial-integrity", title: "Financial Integrity" },
        { id: "reporting", title: "Reporting and Compliance" },
        { id: "enforcement", title: "Enforcement and Disciplinary Action" },
        { id: "contact", title: "Contact Information" }
    ];

    return (
        <div className="employee-code-of-conduct-page">
            <SectionInnerPageBanner _data={bannerData} _type={BANNER_TYPE.COMPACT} />
            
            <PolicyLayout title="Employee Code of Conduct" sections={sections}>

                <div id="introduction" className="section">
                    <h2>1. Introduction</h2>
                    <p>
                        This Employee Code of Conduct establishes the ethical and professional standards expected of all 
                        Anathoth Technologies Ltd employees, contractors, and associates. Our reputation depends on each 
                        team member's commitment to integrity, excellence, and responsible conduct.
                    </p>
                    <p>
                        As a technology solutions provider, we hold ourselves to the highest standards of professional 
                        conduct, technical excellence, and ethical behavior. This code serves as a guide for decision-making 
                        and establishes clear expectations for all team members.
                    </p>
                </div>

                <div id="core-values" className="section">
                    <h2>2. Core Values</h2>
                    
                    <h3>2.1 Integrity</h3>
                    <p>We conduct business with honesty, transparency, and ethical behavior. All employees must:</p>
                    <ul>
                        <li>Be truthful in all business communications</li>
                        <li>Honor commitments and deliver on promises</li>
                        <li>Admit mistakes and take responsibility for corrective action</li>
                        <li>Avoid conflicts of interest and disclose potential conflicts</li>
                        <li>Comply with all applicable laws and regulations</li>
                    </ul>

                    <h3>2.2 Excellence</h3>
                    <p>We strive for excellence in everything we do:</p>
                    <ul>
                        <li>Deliver high-quality work that meets or exceeds expectations</li>
                        <li>Continuously improve skills and knowledge</li>
                        <li>Seek innovative solutions to client challenges</li>
                        <li>Maintain professional certifications and technical competencies</li>
                        <li>Take pride in our work and company reputation</li>
                    </ul>

                    <h3>2.3 Respect</h3>
                    <p>We treat all individuals with dignity and respect:</p>
                    <ul>
                        <li>Value diversity and inclusion in all interactions</li>
                        <li>Communicate professionally and courteously</li>
                        <li>Listen actively and consider different perspectives</li>
                        <li>Provide constructive feedback and support</li>
                        <li>Maintain confidentiality and respect privacy</li>
                    </ul>

                    <h3>2.4 Collaboration</h3>
                    <p>We work together to achieve common goals:</p>
                    <ul>
                        <li>Share knowledge and expertise freely</li>
                        <li>Support team members and contribute to team success</li>
                        <li>Communicate openly and transparently</li>
                        <li>Participate actively in team activities and meetings</li>
                        <li>Resolve conflicts constructively and professionally</li>
                    </ul>
                </div>

                <div id="professional-standards" className="section">
                    <h2>3. Professional Standards</h2>
                    
                    <h3>3.1 Quality of Work</h3>
                    <ul>
                        <li>Deliver work that meets agreed specifications and quality standards</li>
                        <li>Follow established development methodologies and best practices</li>
                        <li>Conduct thorough testing and quality assurance</li>
                        <li>Document work clearly and maintain comprehensive records</li>
                        <li>Meet deadlines and communicate proactively about potential delays</li>
                    </ul>

                    <h3>3.2 Professional Development</h3>
                    <ul>
                        <li>Stay current with industry trends and emerging technologies</li>
                        <li>Participate in training and professional development opportunities</li>
                        <li>Maintain relevant certifications and technical skills</li>
                        <li>Share knowledge through mentoring and knowledge transfer</li>
                        <li>Contribute to the professional growth of colleagues</li>
                    </ul>

                    <h3>3.3 Time Management</h3>
                    <ul>
                        <li>Use work time efficiently and productively</li>
                        <li>Prioritize tasks based on business needs and deadlines</li>
                        <li>Communicate schedule constraints and availability clearly</li>
                        <li>Balance multiple projects and responsibilities effectively</li>
                        <li>Respect colleagues' time and meeting schedules</li>
                    </ul>
                </div>

                <div id="client-relations" className="section">
                    <h2>4. Client Relations</h2>
                    
                    <h3>4.1 Client Service Excellence</h3>
                    <ul>
                        <li>Provide responsive, professional, and courteous service</li>
                        <li>Understand and address client needs and expectations</li>
                        <li>Communicate clearly and keep clients informed of progress</li>
                        <li>Handle client complaints and concerns promptly and fairly</li>
                        <li>Exceed client expectations wherever possible</li>
                    </ul>

                    <h3>4.2 Professional Representation</h3>
                    <ul>
                        <li>Represent Anathoth Technologies professionally at all times</li>
                        <li>Dress appropriately for client meetings and interactions</li>
                        <li>Use professional language and maintain appropriate boundaries</li>
                        <li>Respect client confidentiality and proprietary information</li>
                        <li>Follow proper channels for client communication</li>
                    </ul>

                    <h3>4.3 Relationship Management</h3>
                    <ul>
                        <li>Build and maintain positive long-term client relationships</li>
                        <li>Be responsive to client inquiries and requests</li>
                        <li>Proactively identify opportunities to add value</li>
                        <li>Handle conflicts or disagreements professionally</li>
                        <li>Seek feedback and use it to improve service delivery</li>
                    </ul>
                </div>

                <div id="confidentiality" className="section">
                    <h2>5. Confidentiality and Data Protection</h2>
                    
                    <h3>5.1 Confidential Information</h3>
                    <ul>
                        <li>Protect all confidential and proprietary information</li>
                        <li>Do not disclose sensitive information to unauthorized parties</li>
                        <li>Use confidential information only for authorized business purposes</li>
                        <li>Return or destroy confidential materials upon request or termination</li>
                        <li>Report suspected breaches of confidentiality immediately</li>
                    </ul>

                    <h3>5.2 Client Data Protection</h3>
                    <ul>
                        <li>Handle client data in accordance with privacy laws and regulations</li>
                        <li>Use secure methods for data transmission and storage</li>
                        <li>Access client data only when necessary for work purposes</li>
                        <li>Implement appropriate security measures to protect data</li>
                        <li>Report data security incidents immediately</li>
                    </ul>

                    <h3>5.3 Intellectual Property</h3>
                    <ul>
                        <li>Respect intellectual property rights of clients and third parties</li>
                        <li>Do not use unauthorized software or materials</li>
                        <li>Properly attribute and license all third-party components</li>
                        <li>Protect company intellectual property and trade secrets</li>
                        <li>Follow proper procedures for open-source software usage</li>
                    </ul>
                </div>

                <div id="conflict-of-interest" className="section">
                    <h2>6. Conflict of Interest</h2>
                    
                    <h3>6.1 Identifying Conflicts</h3>
                    <p>A conflict of interest occurs when personal interests interfere with professional duties. Examples include:</p>
                    <ul>
                        <li>Financial interests in client companies or competitors</li>
                        <li>Outside employment that competes with company business</li>
                        <li>Personal relationships that affect business decisions</li>
                        <li>Gifts or benefits that could influence judgment</li>
                        <li>Use of company resources for personal gain</li>
                    </ul>

                    <h3>6.2 Disclosure Requirements</h3>
                    <ul>
                        <li>Disclose all potential conflicts of interest to management</li>
                        <li>Seek approval before engaging in outside business activities</li>
                        <li>Report any changes in circumstances that could create conflicts</li>
                        <li>Recuse yourself from decisions where conflicts exist</li>
                        <li>Follow company procedures for managing disclosed conflicts</li>
                    </ul>

                    <h3>6.3 Outside Activities</h3>
                    <ul>
                        <li>Obtain approval before accepting board positions or consulting roles</li>
                        <li>Ensure outside activities do not interfere with work performance</li>
                        <li>Do not use company resources for personal business activities</li>
                        <li>Avoid activities that compete with company business</li>
                        <li>Maintain clear separation between personal and professional activities</li>
                    </ul>
                </div>

                <div id="workplace-behavior" className="section">
                    <h2>7. Workplace Behavior</h2>
                    
                    <h3>7.1 Professional Conduct</h3>
                    <ul>
                        <li>Treat all colleagues with respect and dignity</li>
                        <li>Maintain a professional demeanor in all interactions</li>
                        <li>Use appropriate language and avoid offensive behavior</li>
                        <li>Respect cultural differences and promote inclusion</li>
                        <li>Contribute to a positive and productive work environment</li>
                    </ul>

                    <h3>7.2 Harassment and Discrimination</h3>
                    <ul>
                        <li>Do not engage in harassment, discrimination, or bullying</li>
                        <li>Report any incidents of inappropriate behavior</li>
                        <li>Support company diversity and inclusion initiatives</li>
                        <li>Treat all individuals fairly regardless of personal characteristics</li>
                        <li>Create an inclusive environment where all can succeed</li>
                    </ul>

                    <h3>7.3 Health and Safety</h3>
                    <ul>
                        <li>Follow all health and safety policies and procedures</li>
                        <li>Maintain a safe and secure work environment</li>
                        <li>Report safety hazards and security concerns immediately</li>
                        <li>Use proper ergonomics and take breaks as needed</li>
                        <li>Support company wellness initiatives</li>
                    </ul>
                </div>

                <div id="technology-use" className="section">
                    <h2>8. Technology and Resource Use</h2>
                    
                    <h3>8.1 Acceptable Use</h3>
                    <ul>
                        <li>Use company technology and resources for authorized business purposes</li>
                        <li>Follow all IT policies and security procedures</li>
                        <li>Protect login credentials and access privileges</li>
                        <li>Report security incidents and suspicious activities</li>
                        <li>Use appropriate software licensing and avoid piracy</li>
                    </ul>

                    <h3>8.2 Personal Use</h3>
                    <ul>
                        <li>Limited personal use of company resources is permitted</li>
                        <li>Personal use must not interfere with work responsibilities</li>
                        <li>Do not access inappropriate or offensive content</li>
                        <li>Maintain professionalism in all online activities</li>
                        <li>Respect bandwidth and storage limitations</li>
                    </ul>

                    <h3>8.3 Social Media</h3>
                    <ul>
                        <li>Use good judgment when posting about work-related topics</li>
                        <li>Do not disclose confidential information on social platforms</li>
                        <li>Clearly identify personal opinions as your own</li>
                        <li>Avoid controversial or offensive content</li>
                        <li>Respect colleagues' privacy and professional reputation</li>
                    </ul>
                </div>

                <div id="financial-integrity" className="section">
                    <h2>9. Financial Integrity</h2>
                    
                    <h3>9.1 Expense Management</h3>
                    <ul>
                        <li>Submit accurate and timely expense reports</li>
                        <li>Provide proper documentation for all expenses</li>
                        <li>Use company resources efficiently and cost-effectively</li>
                        <li>Seek approval for expenses outside normal guidelines</li>
                        <li>Avoid personal use of company credit cards or accounts</li>
                    </ul>

                    <h3>9.2 Financial Reporting</h3>
                    <ul>
                        <li>Provide accurate information for financial reporting</li>
                        <li>Report time and project costs accurately</li>
                        <li>Follow proper procedures for invoicing and billing</li>
                        <li>Maintain accurate records of work performed</li>
                        <li>Cooperate with audits and financial reviews</li>
                    </ul>

                    <h3>9.3 Gifts and Entertainment</h3>
                    <ul>
                        <li>Do not accept gifts that could influence business decisions</li>
                        <li>Report any substantial gifts or entertainment offers</li>
                        <li>Maintain appropriate business relationships with vendors</li>
                        <li>Avoid situations that could appear improper</li>
                        <li>Follow company guidelines for client entertainment</li>
                    </ul>
                </div>

                <div id="reporting" className="section">
                    <h2>10. Reporting and Compliance</h2>
                    
                    <h3>10.1 Reporting Violations</h3>
                    <ul>
                        <li>Report suspected violations of this code promptly</li>
                        <li>Use appropriate channels for reporting concerns</li>
                        <li>Provide accurate and complete information</li>
                        <li>Cooperate with investigations and inquiries</li>
                        <li>Do not retaliate against those who report in good faith</li>
                    </ul>

                    <h3>10.2 Reporting Channels</h3>
                    <ul>
                        <li>Direct supervisor or management team</li>
                        <li>Human Resources (Karen Lusimbo)</li>
                        <li>Chief Executive Officer (Ezer Masaba)</li>
                        <li>Anonymous reporting through designated channels</li>
                        <li>External regulatory authorities (when appropriate)</li>
                    </ul>

                    <h3>10.3 Investigation Process</h3>
                    <ul>
                        <li>All reports will be investigated promptly and fairly</li>
                        <li>Confidentiality will be maintained to the extent possible</li>
                        <li>Appropriate corrective action will be taken when warranted</li>
                        <li>Protection against retaliation will be provided</li>
                        <li>Results will be communicated as appropriate</li>
                    </ul>
                </div>

                <div id="enforcement" className="section">
                    <h2>11. Enforcement and Disciplinary Action</h2>
                    
                    <h3>11.1 Disciplinary Measures</h3>
                    <p>Violations of this code may result in disciplinary action, including:</p>
                    <ul>
                        <li>Verbal or written warnings</li>
                        <li>Additional training or supervision</li>
                        <li>Performance improvement plans</li>
                        <li>Suspension with or without pay</li>
                        <li>Termination of employment</li>
                        <li>Legal action (when appropriate)</li>
                    </ul>

                    <h3>11.2 Factors Considered</h3>
                    <p>Disciplinary decisions will consider:</p>
                    <ul>
                        <li>Severity and nature of the violation</li>
                        <li>Impact on clients, colleagues, and company</li>
                        <li>Employee's past performance and conduct record</li>
                        <li>Intent and circumstances surrounding the violation</li>
                        <li>Cooperation with investigation and remediation efforts</li>
                    </ul>

                    <h3>11.3 Appeals Process</h3>
                    <ul>
                        <li>Employees may appeal disciplinary decisions through established procedures</li>
                        <li>Appeals should be submitted in writing with supporting documentation</li>
                        <li>A fair and impartial review process will be conducted</li>
                        <li>Final decisions will be communicated in writing</li>
                        <li>External review may be available in certain circumstances</li>
                    </ul>
                </div>

                <div id="contact" className="section">
                    <h2>12. Contact Information</h2>
                    
                    <h3>Questions and Guidance</h3>
                    <ul>
                        <li><strong>Human Resources:</strong> Karen Lusimbo - karen.lusimbo@theanathoth.com</li>
                        <li><strong>CEO:</strong> Ezer Masaba - ezer.masaba@theanathoth.com</li>
                        <li><strong>COO:</strong> Vivian Agenorwot - vivian.agenorwot@theanathoth.com</li>
                        <li><strong>General inquiries:</strong> info@theanathoth.com</li>
                    </ul>

                    <h3>Reporting Concerns</h3>
                    <ul>
                        <li><strong>Ethics hotline:</strong> ethics@theanathoth.com</li>
                        <li><strong>Phone:</strong> +256 744 666608</li>
                        <li><strong>Office hours:</strong> Monday-Friday, 8 AM - 6 PM EAT</li>
                        <li><strong>Office address:</strong> 1 Kololo Hill Drive, Kampala, Uganda</li>
                    </ul>

                    <h3>Acknowledgment</h3>
                    <p>
                        All employees are required to read, understand, and acknowledge their commitment to 
                        following this Employee Code of Conduct. By continuing employment with Anathoth 
                        Technologies Ltd, you agree to abide by these standards and report any violations 
                        that come to your attention.
                    </p>

                    <p><em>Last updated on 01 January, 2025</em></p>
                </div>
            </PolicyLayout>
        </div>
    );
};

export default EmployeeCodeOfConductPage; 